import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { UploadControlV2 } from '@client/components/_shared/documents/UploadControlV2';
import { CostCodeSelect } from '@client/components/_shared/selectors/financial/CostCodeSelect';
import { TransactionTypeSelect } from '@client/components/_shared/selectors/financial/TransactionTypeSelect';
import { SupplierSelect } from '@client/components/_shared/selectors/suppliers/SupplierSelect';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { PercentageInput } from '@client/components/_ui/data-entry/PercentageInput';
import { PhoneNumberInput, phoneNumberInputRule } from '@client/components/_ui/data-entry/PhoneNumberInput';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { AbnInput } from '@client/components/claims/financials/AbnInput';
import { accountBsbInputRule, BsbInput } from '@client/components/claims/financials/BsbInput';
import { CompensationPaymentTable } from '@client/components/claims/financials/CompensationPaymentTable';
import { CompensationSummary } from '@client/components/claims/financials/CompensationSummary';
import { ExcessPaymentTable } from '@client/components/claims/financials/ExcessPaymentTable';
import { PAY_ID_TYPES_LABELS } from '@client/components/claims/financials/payment-account/PaymentAccountFields';
import { ContactSelect } from '@client/components/contacts/ContactSelect';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { abnInputRule, cn } from '@client/utils';
import { capture } from '@client/utils/analytics';
import { buildPercentageInputRules } from '@client/utils/validation/percentage';
import { COMPENSATION_PAYEE_TYPES, type CompensationPayeeType } from '@shared/constants/business/compensation';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Alert, Divider, Form, type FormInstance, Input, Select, Steps, Switch } from 'antd';
import Decimal from 'decimal.js';
import { Check, CreditCard, HandCoins, Landmark, Upload, User, Wallet, WalletCards } from 'lucide-react';
import { useEffect, useState } from 'react';
import { claimFormStore } from '../../../store';
import { type Compensation, useCompensationStore } from './store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/compensations/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add payment',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationAdd />
        </PageController>
    );
}

const COMPENSATION_PAYEE_TYPE_LABELS = {
    POLICY_HOLDER: 'Policy holder',
    OTHER_CONTACT: 'Third party',
    SUPPLIER: 'Supplier',
} as const satisfies Record<CompensationPayeeType, string>;

const FormItem = Form.Item<Compensation>;

const EMPTY_PAYMENT_ACCOUNT_FIELDS = {
    paymentMethod: 'PAY_ID',
    payIdType: undefined,
    payIdValue: undefined,
    accountName: undefined,
    accountBsb: undefined,
    accountNo: undefined,
    isInternationalSupplier: undefined,
    isRegisteredForGst: undefined,
    inputTaxCreditEntitlement: undefined,
    abn: undefined,
    remittanceEmail: undefined,
} satisfies Partial<Compensation>;

/** The payee, payment account and tax fields of the form, as the create and update mutations take them. */
function toCompensationPayload(compensation: Compensation) {
    return {
        payeeType: compensation.payeeType,
        paymentMethod: compensation.paymentMethod,
        contactId: compensation.contactId,
        supplierId: compensation.supplierId,
        transactionTypeId: compensation.transactionTypeId,
        costCodeId: compensation.costCodeId,
        invoiceDate: compensation.invoiceDate,
        description: compensation.description,
        payIdType: compensation.payIdType,
        payIdValue: compensation.payIdValue || undefined,
        accountBsb: compensation.accountBsb || undefined,
        accountName: compensation.accountName,
        accountNo: compensation.accountNo,
        isRegisteredForGst: compensation.isRegisteredForGst ?? undefined,
        inputTaxCreditEntitlement: compensation.inputTaxCreditEntitlement || undefined,
        abn: compensation.abn || undefined,
        remittanceEmail: compensation.remittanceEmail,
    };
}

function toCompensationFilesPayload(compensation: Compensation) {
    return compensation.files?.map((item) => ({
        key: item.key,
        name: item.name,
        description: item.description,
        categories: item.categories,
        mimeType: item.mimeType,
        size: item.size,
        additionalInfo: item.additionalInfo,
    }));
}

function PaymentAccountFields({
    entity,
    entityId,
    isDisabled,
    onBlockingErrorChange,
}: {
    entity: 'POLICY_HOLDER' | 'OTHER_CONTACT' | 'SUPPLIER';
    entityId: bigint;
    isDisabled?: boolean;
    onBlockingErrorChange: (hasBlockingError: boolean) => void;
}) {
    const { data, error, refetch, isFetching } = trpc.paymentAccount.getPaymentAccount.useQuery(
        entity && entityId
            ? {
                  entity,
                  entityId,
              }
            : skipToken,
        {
            meta: { errorSurface: 'inline' },
            refetchOnWindowFocus: false,
        },
    );

    const form = Form.useFormInstance<Compensation>();
    const paymentMethod = Form.useWatch(({ paymentMethod }) => paymentMethod, form);
    const payIdType = Form.useWatch(({ payIdType }) => payIdType, form);
    const isInternationalSupplier = Form.useWatch(({ isInternationalSupplier }) => isInternationalSupplier, form);
    const isRegisteredForGst = Form.useWatch(({ isRegisteredForGst }) => isRegisteredForGst, form);
    const hasBlockingError = hasBlockingQueryError(error, data);

    useEffect(() => {
        form.setFieldsValue(EMPTY_PAYMENT_ACCOUNT_FIELDS);
    }, [entity, entityId, form]);

    useEffect(() => {
        if (data) {
            let inputTaxCreditEntitlement: Decimal | undefined;

            if ('inputTaxCreditEntitlement' in data) {
                try {
                    inputTaxCreditEntitlement = new Decimal(data.inputTaxCreditEntitlement);
                } catch {
                    inputTaxCreditEntitlement = undefined;
                }
            }

            form.setFieldsValue({
                paymentMethod:
                    !data.payIdType && !data.payIdValue && (data.accountName || data.accountBsb || data.accountNo)
                        ? 'BANK_ACCOUNT'
                        : 'PAY_ID',
                payIdType: data.payIdType,
                payIdValue: data.payIdValue,
                accountName: data.accountName,
                accountBsb: data.accountBsb,
                accountNo: data.accountNo,
                // @ts-expect-error temporary fix
                isInternationalSupplier: data.isInternationalSupplier,
                isRegisteredForGst: data.isRegisteredForGst,
                inputTaxCreditEntitlement,
                abn: data.abn,
                // @ts-expect-error temporary fix
                remittanceEmail: data.remittanceEmail,
            });
        }
    }, [data, form, hasBlockingError]);

    useEffect(() => {
        if (hasBlockingError) {
            form.setFieldsValue(EMPTY_PAYMENT_ACCOUNT_FIELDS);
        }
    }, [form, hasBlockingError]);

    useEffect(() => {
        onBlockingErrorChange(hasBlockingError);
    }, [hasBlockingError, onBlockingErrorChange]);

    if (hasBlockingError) {
        return (
            <InlineQueryError
                error={error}
                onRetry={() => void refetch()}
                retrying={isFetching}
                title="Couldn't load payment account details"
                className="mb-4"
            />
        );
    }

    return (
        <>
            {error && (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't refresh payment account details"
                    description="Showing the last loaded payment account details — try again to refresh."
                    compact
                    className="mb-4"
                />
            )}
            <SectionHeader
                icon={CreditCard}
                title="Payment method"
                description="Select how the payee will receive this payment"
            />
            <FormItem name="paymentMethod" hidden />
            <div className="mb-5 flex gap-3">
                {(
                    [
                        {
                            icon: Wallet,
                            label: 'PayID',
                            description: 'Pay using email, phone or ABN',
                            value: 'PAY_ID',
                        },
                        {
                            icon: Landmark,
                            label: 'Bank account',
                            description: 'Pay directly to bank account',
                            value: 'BANK_ACCOUNT',
                        },
                    ] satisfies {
                        value: Compensation['paymentMethod'];
                        label: string;
                        description: string;
                        icon: typeof User;
                    }[]
                ).map((option) => {
                    const isSelected = paymentMethod === option.value;
                    const Icon = option.icon;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => form.setFieldsValue({ paymentMethod: option.value })}
                            className={cn(
                                'relative flex flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 p-4 text-left transition-all',
                                isSelected
                                    ? 'border-accent-500 bg-accent-50'
                                    : 'border-separator bg-card hover:border-gray-300',
                                isDisabled && 'cursor-not-allowed opacity-50',
                            )}
                        >
                            <div
                                className={cn(
                                    'flex size-10 items-center justify-center rounded-lg',
                                    isSelected ? 'bg-accent-100' : 'bg-muted',
                                )}
                            >
                                <Icon className={cn('size-5', isSelected ? 'text-accent-600' : 'text-gray-500')} />
                            </div>
                            <div className="flex-1">
                                <span className="font-medium text-foreground text-sm">{option.label}</span>
                                <p className="text-muted-foreground text-xs">{option.description}</p>
                            </div>
                            {isSelected && (
                                <div className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full bg-accent-500">
                                    <Check className="size-3 text-white" />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {paymentMethod === 'PAY_ID' ? (
                <div className="grid grid-cols-2 gap-x-4">
                    <FormItem name="payIdType" label="PayID type" rules={[{ required: true }]}>
                        <Select
                            allowClear
                            disabled={isDisabled}
                            options={Object.entries(PAY_ID_TYPES_LABELS).map(([key, value]) => ({
                                label: value,
                                value: key,
                            }))}
                            onClick={(e) => e.preventDefault()}
                            onChange={(value) => {
                                form.setFieldsValue(
                                    value
                                        ? { payIdValue: null }
                                        : {
                                              payIdType: null,
                                              payIdValue: null,
                                          },
                                );
                            }}
                        />
                    </FormItem>
                    {payIdType === 'EMAIL' && (
                        <FormItem label="Email" name="payIdValue" rules={[{ required: true }, { type: 'email' }]}>
                            <Input disabled={isDisabled} />
                        </FormItem>
                    )}
                    {payIdType === 'PHONE' && (
                        <FormItem label="Phone" name="payIdValue" rules={[{ required: true }, phoneNumberInputRule]}>
                            <PhoneNumberInput isDisabled={isDisabled} />
                        </FormItem>
                    )}
                    {payIdType === 'ABN' && (
                        <FormItem label="ABN" name="payIdValue" rules={[{ required: true }, abnInputRule]}>
                            <AbnInput disabled={isDisabled} />
                        </FormItem>
                    )}
                    {payIdType === 'ORGANIZATION_ID' && (
                        <FormItem label="Organisation ID" name="payIdValue" rules={[{ required: true }]}>
                            <Input maxLength={20} disabled={isDisabled} />
                        </FormItem>
                    )}
                </div>
            ) : (
                <>
                    <FormItem label="Account name" name="accountName" rules={[{ required: true }]}>
                        <Input disabled={isDisabled} />
                    </FormItem>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormItem label="BSB" name="accountBsb" rules={[{ required: true }, accountBsbInputRule]}>
                            <BsbInput disabled={isDisabled} />
                        </FormItem>
                        <FormItem label="Account number" name="accountNo" rules={[{ required: true }]}>
                            <Input disabled={isDisabled} />
                        </FormItem>
                    </div>
                </>
            )}
            <Divider className="mt-2" />
            <SectionHeader
                icon={HandCoins}
                title="Tax information"
                description="Provide GST registration and tax details for this payee"
            />
            {entity === 'SUPPLIER' && (
                <FormItem label="International supplier" name="isInternationalSupplier">
                    <Switch
                        onChange={(value) => {
                            if (value) {
                                form.setFieldsValue({
                                    isRegisteredForGst: false,
                                    abn: undefined,
                                });
                            }
                        }}
                        disabled={isDisabled}
                    />
                </FormItem>
            )}
            {!isInternationalSupplier && (
                <FormItem label="Registered for GST" name="isRegisteredForGst">
                    <Switch
                        onChange={() => {
                            if (entity === 'POLICY_HOLDER' || entity === 'OTHER_CONTACT') {
                                form.setFieldsValue({
                                    inputTaxCreditEntitlement: new Decimal(100),
                                });
                            }
                        }}
                        disabled={isDisabled}
                    />
                </FormItem>
            )}
            <div className="grid grid-cols-2 gap-x-4">
                {!isInternationalSupplier && (
                    <FormItem
                        label="ABN"
                        name="abn"
                        rules={[{ required: entity === 'SUPPLIER' && !!isRegisteredForGst }, abnInputRule]}
                    >
                        <AbnInput disabled={isDisabled} />
                    </FormItem>
                )}
                {(entity === 'POLICY_HOLDER' || entity === 'OTHER_CONTACT') && isRegisteredForGst && (
                    <FormItem
                        label="ITC entitlement %"
                        name="inputTaxCreditEntitlement"
                        rules={buildPercentageInputRules({ isRequired: true })}
                    >
                        <PercentageInput className="w-full" />
                    </FormItem>
                )}
                {entity === 'SUPPLIER' && (
                    <FormItem label="Remittance email" name="remittanceEmail" rules={[{ type: 'email' }]}>
                        <Input disabled={isDisabled} />
                    </FormItem>
                )}
            </div>
        </>
    );
}

function CompensationFormStep({
    form,
    onValuesChange,
    className,
    onPaymentAccountBlockingErrorChange,
}: {
    form: FormInstance<Compensation>;
    onValuesChange: () => void;
    className?: string;
    onPaymentAccountBlockingErrorChange: (hasBlockingError: boolean) => void;
}) {
    const claim = claimFormStore.use.formData();
    const orgId = useOrgId();

    const payeeType = Form.useWatch(({ payeeType }) => payeeType, form);
    const contactId = Form.useWatch(({ contactId }) => contactId, form);
    const supplierId = Form.useWatch(({ supplierId }) => supplierId, form);
    const transactionTypeId = Form.useWatch(({ transactionTypeId }) => transactionTypeId, form);

    const {
        data: transactionType,
        isLoading,
        error,
        refetch,
        isFetching,
    } = trpc.transactionType.getTransactionType.useQuery(
        payeeType === 'SUPPLIER' && transactionTypeId ? { id: transactionTypeId } : skipToken,
        { meta: { errorSurface: 'inline' } },
    );

    const isDisabled = !payeeType;

    useEffect(() => {
        form.setFields([
            {
                name: 'invoiceDate',
                errors: isLoading ? ['Loading...'] : [],
            },
        ]);
    }, [isLoading]);

    const hasBlockingError = hasBlockingQueryError(error, transactionType);

    return (
        <div className={cn('w-2/3', className)}>
            {error && (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title={
                        hasBlockingError
                            ? "Couldn't load transaction type details"
                            : "Couldn't refresh transaction type details"
                    }
                    description={
                        hasBlockingError
                            ? undefined
                            : 'Showing the last loaded transaction type details — try again to refresh.'
                    }
                    compact={!hasBlockingError}
                    className="mb-4"
                />
            )}
            <Form
                form={form}
                onValuesChange={onValuesChange}
                layout="vertical"
                initialValues={{
                    invoiceDate: new Date(),
                    paymentMethod: 'PAY_ID',
                }}
            >
                <SectionHeader icon={User} title="Payee information" description="Who are you paying?" />
                <FormItem name="payeeType" label="Type" rules={[{ required: true }]}>
                    <Select
                        options={Object.values(COMPENSATION_PAYEE_TYPES).map((item) => ({
                            label: COMPENSATION_PAYEE_TYPE_LABELS[item],
                            value: item,
                        }))}
                        onChange={() =>
                            form.setFieldsValue({
                                supplierId: undefined,
                                contactId: undefined,
                                transactionTypeId: undefined,
                            })
                        }
                    />
                </FormItem>
                {(payeeType === 'POLICY_HOLDER' || payeeType === 'OTHER_CONTACT') && (
                    <FormItem
                        name="contactId"
                        label={`Select ${COMPENSATION_PAYEE_TYPE_LABELS[payeeType]?.toLowerCase() || 'payee'}`}
                        rules={[{ required: true }]}
                    >
                        <ContactSelect
                            type={payeeType}
                            entityId={claim.id}
                            entity="claim"
                            isDisabled={isDisabled}
                            shouldAutoSelectFirstOption={!isDisabled}
                        />
                    </FormItem>
                )}
                {payeeType === 'SUPPLIER' && (
                    <>
                        <FormItem name="supplierId" label="Company" rules={[{ required: true }]}>
                            <SupplierSelect
                                claimId={claim.id}
                                shouldAutoSelectFirstOption={
                                    !isDisabled && {
                                        shouldAutoSelectIfMultiple: false,
                                    }
                                }
                                footer={
                                    <Button.Add.Link
                                        size="small"
                                        variant="tertiary"
                                        to="/console/$orgId/claims/details/$id/financials/compensations/add/suppliers/add"
                                        params={{
                                            orgId,
                                            id: claim.id,
                                        }}
                                        className="flex w-full items-center"
                                        label="New supplier"
                                    />
                                }
                            />
                        </FormItem>
                        <FormItem name="contactId" label="Contact" rules={[{ required: true }]}>
                            <ContactSelect
                                type={payeeType}
                                entityId={claim.id}
                                entity="claim"
                                filters={{ supplierId }}
                                isDisabled={isDisabled || !supplierId}
                                shouldAutoSelectFirstOption={!isDisabled && !!supplierId}
                            />
                        </FormItem>
                    </>
                )}

                <Divider />

                <SectionHeader
                    icon={WalletCards}
                    title="Transaction information"
                    description="Categorize and describe this payment transaction"
                />
                <FormItem name="transactionTypeId" label="Transaction type" rules={[{ required: true }]}>
                    <TransactionTypeSelect
                        shouldAutoSelectFirstOption={
                            !isDisabled && {
                                dependencies: [payeeType],
                                filter(_, option, options) {
                                    return (
                                        // When payee is Supplier - default to the first expense transaction type in the list.
                                        payeeType !== 'SUPPLIER' ||
                                        !options.find((item) => item.data.transactionNature === 'EXPENSE') ||
                                        option.data.transactionNature === 'EXPENSE'
                                    );
                                },
                            }
                        }
                        transactionNature={payeeType === 'SUPPLIER' ? ['EXPENSE', 'INDEMNITY'] : ['INDEMNITY']}
                        productTypeId={claim.productTypeId!}
                        disabled={isDisabled}
                    />
                </FormItem>
                <FormItem name="costCodeId" label="Cost code" rules={[{ required: true }]}>
                    <CostCodeSelect productTypeId={claim.productTypeId!} disabled={isDisabled} />
                </FormItem>
                <FormItem
                    name="invoiceDate"
                    label="Invoice date"
                    rules={[
                        {
                            required:
                                payeeType === 'SUPPLIER' &&
                                Boolean(transactionTypeId) &&
                                (transactionType === undefined || transactionType.transactionNature === 'INDEMNITY'),
                        },
                    ]}
                >
                    <DatePicker className="w-full" disabled={isDisabled} placeholder="" />
                </FormItem>
                <FormItem name="description" label="Description" rules={[{ required: true }]}>
                    <Input.TextArea rows={4} disabled={isDisabled} />
                </FormItem>

                <Divider />

                <PaymentAccountFields
                    entity={payeeType}
                    entityId={
                        payeeType === 'POLICY_HOLDER' ? claim.id : payeeType === 'SUPPLIER' ? supplierId! : contactId
                    }
                    isDisabled={isDisabled}
                    onBlockingErrorChange={onPaymentAccountBlockingErrorChange}
                />

                <Divider />

                <SectionHeader
                    icon={Upload}
                    title="Attachments"
                    description="Upload invoices, receipts, or other supporting documents"
                />
                <UploadControlV2
                    accept=".pdf,.jpeg,.png,.jpg,.doc,.docx"
                    purpose="entityAttachment"
                    onFileListChange={(files) => {
                        const setFields = form.setFieldsValue as unknown as (values: { files: typeof files }) => void;
                        setFields({ files });
                    }}
                    disabled={isDisabled}
                />
                <FormItem name="files" hidden />
            </Form>
        </div>
    );
}

function LinkPaymentsStep({ form, className }: { form: FormInstance<Compensation>; className?: string }) {
    const orgId = useOrgId();
    const claim = claimFormStore.use.formData();
    const compensationId = useCompensationStore((state) => state.compensationId);
    const compensation = useCompensationStore((state) => state.compensation);
    const { data: transactionType } = trpc.transactionType.getTransactionType.useQuery(
        compensation?.transactionTypeId ? { id: compensation.transactionTypeId } : skipToken,
    );

    const payeeType = Form.useWatch(({ payeeType }) => payeeType, form);
    const contactId = Form.useWatch(({ contactId }) => contactId, form);
    const disabled = !payeeType;

    return (
        <div className={className}>
            <Alert
                title="Note: Once a payment item or excess is added, the 'General payment information' section cannot be modified."
                type="warning"
                showIcon
                className="mb-6 max-w-max"
            />

            {compensationId && <CompensationSummary id={compensationId} />}

            <CompensationPaymentTable
                compensationId={compensationId}
                description="List all the items that will form a part of this payment transaction"
                rightAddon={
                    <Button.Add.Link
                        variant="tertiary"
                        disabled={disabled || (payeeType === 'OTHER_CONTACT' && !contactId)}
                        disabledReason={
                            !payeeType
                                ? 'Please select a payee type to add a payment item.'
                                : 'Please select a contact to add a payment item.'
                        }
                        to="/console/$orgId/claims/details/$id/financials/compensations/add/compensation-payments/add"
                        params={{
                            orgId,
                            id: claim.id,
                        }}
                        label="Add payment item"
                    />
                }
            />

            {(payeeType === 'POLICY_HOLDER' ||
                (payeeType === 'SUPPLIER' && transactionType?.transactionNature === 'INDEMNITY')) && (
                <ExcessPaymentTable
                    compensationId={compensationId}
                    className="mt-6"
                    description="List excesses collected by the supplier or deducted from a settlement payment"
                    rightAddon={
                        <Button.Add.Link
                            variant="tertiary"
                            disabled={disabled}
                            disabledReason="Please select a payee type to link an excess."
                            to="/console/$orgId/claims/details/$id/financials/compensations/add/excess-payments/add"
                            params={{
                                orgId,
                                id: claim.id,
                            }}
                            label="Link excess"
                        />
                    }
                />
            )}
        </div>
    );
}

const STEPS = ['compensationAdd', 'addPaymentItems'] as const;

function CompensationAdd() {
    const isReadonly = claimFormStore.use.isReadonly();
    const compensationId = useCompensationStore((state) => state.compensationId);
    const compensationPaymentCount = useCompensationStore((state) => state.compensationPaymentCount);
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { data, isPending } = trpc.reserve.getReserveRemainingAmount.useQuery({ claimId: id });
    const { mutate: createCompensation } = trpc.compensation.createCompensation.useMutation();
    const { mutate: updateCompensation } = trpc.compensation.updateCompensation.useMutation();
    const { mutate: initializeCompensation } = trpc.compensation.initializeCompensation.useMutation();
    const navigate = useNavigate();
    const setCompensation = useCompensationStore((state) => state.setCompensation);
    const reset = useCompensationStore((state) => state.reset);
    const setCompensationId = useCompensationStore((state) => state.setCompensationId);

    const [step, setStep] = useState<(typeof STEPS)[number]>(STEPS[0]);
    const [hasBlockingPaymentAccountError, setHasBlockingPaymentAccountError] = useState(false);

    const [form] = Form.useForm<Compensation>();

    const formDirty = useFormDirty();

    useEffect(() => {
        if (isReadonly || data?.remainingReserveAmount.lte(0)) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly, data?.remainingReserveAmount]);

    if (isReadonly || isPending || data?.remainingReserveAmount.lte(0)) {
        return null;
    }

    function scrollToTop() {
        const overlayPageBody = document.querySelector('.ant-drawer-body');
        overlayPageBody?.scrollTo({ top: 0 });
    }

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title={<CardTitle icon={<ICONS.payment />} title="Add an indemnity and expense payment" />}
            size="large"
            afterClose={() => {
                form.resetFields();
                reset();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose, requestClose }) => (
                <>
                    <Button onClick={requestClose}>Cancel</Button>
                    {step === 'compensationAdd' && (
                        <>
                            <Button disabled>Previous</Button>
                            <Button
                                type="primary"
                                disabled={hasBlockingPaymentAccountError}
                                onClick={async () => {
                                    await form.validateFields();

                                    const compensation = form.getFieldsValue();

                                    setCompensation(compensation);

                                    if (compensationId) {
                                        updateCompensation(
                                            {
                                                id: compensationId,
                                                set: toCompensationPayload(compensation),
                                                files: toCompensationFilesPayload(compensation) ?? [],
                                            },
                                            {
                                                onSuccess() {
                                                    setStep('addPaymentItems');
                                                    scrollToTop();
                                                },
                                                onError(error) {
                                                    errorMessage.show(error);
                                                },
                                            },
                                        );
                                    } else {
                                        createCompensation(
                                            {
                                                claimId: id,
                                                ...toCompensationPayload(compensation),
                                                files: toCompensationFilesPayload(compensation),
                                            },
                                            {
                                                onSuccess(data) {
                                                    setStep('addPaymentItems');
                                                    scrollToTop();
                                                    setCompensationId(data.id);
                                                },
                                                onError(error) {
                                                    errorMessage.show(error);
                                                },
                                            },
                                        );
                                    }
                                }}
                            >
                                Next
                            </Button>
                        </>
                    )}
                    {step === 'addPaymentItems' && (
                        <>
                            <Button
                                onClick={() => {
                                    setStep('compensationAdd');
                                    scrollToTop();
                                }}
                                disabled={!!compensationPaymentCount}
                            >
                                Previous
                            </Button>
                            <Tooltip
                                title={compensationPaymentCount ? undefined : 'Add at least one payment item to save.'}
                            >
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!compensationPaymentCount}
                                    onClick={() => {
                                        if (!compensationId) {
                                            return;
                                        }

                                        message.loading('Saving...');

                                        initializeCompensation(
                                            { id: compensationId },
                                            {
                                                onSuccess() {
                                                    message.success('Saved.');
                                                    capture('compensation_created', { module: 'claim' });
                                                    void trpcUtils.claim.getFinancialSummary.invalidate();
                                                    void trpcUtils.reserve.invalidate();
                                                    void trpcUtils.excess.invalidate();
                                                    void trpcUtils.compensation.invalidate();
                                                    onClose();
                                                },
                                                onError(error) {
                                                    errorMessage.show(error);
                                                },
                                            },
                                        );
                                    }}
                                >
                                    Save
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </>
            )}
        >
            <Outlet />
            <Steps
                current={STEPS.indexOf(step)}
                size="small"
                className="mb-6 w-2/3 pt-0"
                type="navigation"
                items={[
                    {
                        title: 'General payment information',
                        disabled: !!compensationPaymentCount,
                    },
                    {
                        title: 'Payment items and excesses',
                        disabled: hasBlockingPaymentAccountError,
                    },
                ]}
                onChange={async (newStep) => {
                    if (step === 'compensationAdd') {
                        if (hasBlockingPaymentAccountError) {
                            return;
                        }

                        await form.validateFields();
                        setCompensation(form.getFieldsValue());
                    }

                    if (step === 'addPaymentItems' && compensationPaymentCount) {
                        return;
                    }

                    setStep(STEPS[newStep]);
                    scrollToTop();
                }}
            />
            <CompensationFormStep
                form={form}
                onValuesChange={formDirty.markDirty}
                className={cn(step !== 'compensationAdd' && 'hidden')}
                onPaymentAccountBlockingErrorChange={setHasBlockingPaymentAccountError}
            />
            <LinkPaymentsStep form={form} className={cn(step !== 'addPaymentItems' && 'hidden')} />
        </OverlayPage>
    );
}
